import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Statistic, Row, Col, Tag, Image, message, Space, Progress } from 'antd';
import { ReloadOutlined, ShoppingCartOutlined, RocketOutlined } from '@ant-design/icons';
import supplyChainService, { ReplenishmentSuggestion } from '../../../services/supplyChainService';

const ReplenishmentDashboard: React.FC = () => {
    const [suggestions, setSuggestions] = useState<ReplenishmentSuggestion[]>([]);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);

    useEffect(() => {
        fetchSuggestions();
    }, []);

    const fetchSuggestions = async () => {
        setLoading(true);
        try {
            const res = await supplyChainService.getReplenishmentSuggestions({ is_processed: false });
            setSuggestions(res.data);
        } catch (error) {
            message.error('加载补货建议失败');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            const res = await supplyChainService.generateReplenishmentSuggestions();
            message.success(res.data.message);
            fetchSuggestions();
        } catch (error) {
            message.error('生成失败');
        } finally {
            setGenerating(false);
        }
    };

    const columns = [
        {
            title: '商品', dataIndex: 'seller_sku', key: 'product', width: 250,
            render: (_: any, record: ReplenishmentSuggestion) => (
                <Space>
                    <Image src={record.main_image} width={50} />
                    <div>
                        <div style={{ fontWeight: 'bold' }}>{record.seller_sku_code}</div>
                        <div style={{ fontSize: '12px', color: '#888' }}>{record.seller_sku_name}</div>
                    </div>
                </Space>
            )
        },
        {
            title: '7日销量 (日均)', dataIndex: 'avg_daily_sales_7d', key: 'velocity',
            render: (val: number) => <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{val}</span>
        },
        {
            title: '库存状态', key: 'stock',
            render: (_: any, record: ReplenishmentSuggestion) => (
                <div>
                    <div>现贷: {record.current_stock}</div>
                    <div>在途: {record.inbound_stock}</div>
                </div>
            )
        },
        {
            title: '可售天数', dataIndex: 'days_of_supply', key: 'dos',
            render: (val: number) => {
                let color = 'green';
                if (val < 30) color = 'red';
                else if (val < 60) color = 'orange';
                return <Tag color={color}>{Number(val).toFixed(1)} 天</Tag>;
            }
        },
        {
            title: '建议补货', dataIndex: 'suggested_restock_qty', key: 'qty',
            render: (val: number) => <span style={{ fontSize: '16px', fontWeight: 'bold', color: 'red' }}>{val}</span>
        },
        {
            title: '操作', key: 'action',
            render: (_: any, record: ReplenishmentSuggestion) => (
                <Space>
                    <Button type="primary" size="small" icon={<ShoppingCartOutlined />}>采购</Button>
                    <Button size="small" icon={<RocketOutlined />}>发货</Button>
                </Space>
            )
        }
    ];

    return (
        <div style={{ padding: '24px' }}>
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={8}>
                    <Card>
                        <Statistic title="待补货 SKU" value={suggestions.length} valueStyle={{ color: '#cf1322' }} prefix={<ShoppingCartOutlined />} />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Statistic title="总建议补货量" value={suggestions.reduce((acc, curr) => acc + curr.suggested_restock_qty, 0)} suffix="件" />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Button type="primary" size="large" icon={<ReloadOutlined />} loading={generating} onClick={handleGenerate} block style={{ height: '100%' }}>
                            运行智能补货分析
                        </Button>
                    </Card>
                </Col>
            </Row>

            <Card title="智能补货建议">
                <Table dataSource={suggestions} columns={columns} rowKey="id" loading={loading} />
            </Card>
        </div>
    );
};

export default ReplenishmentDashboard;
